/**
 * FleetManager V3 - Alerts Widget
 * Priority alerts requiring action - clickable items navigate to relevant views
 *
 * i18n : une alerte porte des clés (`titleKey`/`titleParams`, `subtitleKey`/
 * `subtitleParams`, `actionKey`) émises par useDashboardData ; le widget les
 * résout au rendu. Compat : un item qui porte encore `title` / `subtitle` /
 * `action` en clair est affiché tel quel.
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Bell, ChevronDown, ChevronRight, Users, Shield, Wrench, AlertTriangle, FileText } from 'lucide-react';
import { Card } from '../../shared/index.js';
import { Badge } from '../../shared/index.js';

const ALERT_ICONS = {
  license: Users,
  insurance: Shield,
  maintenance: Wrench,
  claim: AlertTriangle,
  document: FileText
};

// Map alert categories to navigation targets
const ALERT_NAVIGATION = {
  license: 'drivers',
  insurance: 'insurance',
  maintenance: 'maintenance',
  claim: 'claims',
  document: 'documents'
};

/** Résout « clé + params » ou repli sur le texte en clair. */
function resolve(t, key, params, plain) {
  if (key) return t(key, params);
  return plain ?? '';
}

function AlertItem({ alert, isLast, onNavigate }) {
  const { t } = useTranslation();
  const Icon = ALERT_ICONS[alert.category] || AlertTriangle;
  const isCritical = alert.type === 'critical';
  const targetView = ALERT_NAVIGATION[alert.category] || 'dashboard';

  const title = resolve(t, alert.titleKey, alert.titleParams, alert.title);
  const subtitle = resolve(t, alert.subtitleKey, alert.subtitleParams, alert.subtitle);
  const action = resolve(t, alert.actionKey, alert.actionParams, alert.action);

  const handleClick = () => {
    onNavigate?.(targetView, { alertId: alert.id });
  };

  return (
    <div
      onClick={handleClick}
      className={`
        flex items-center gap-2 p-2.5 hover:bg-gray-50 dark:hover:bg-slate-700/30 transition-colors cursor-pointer group
        ${!isLast ? 'border-b border-gray-100 dark:border-slate-700/30' : ''}
      `}
    >
      <div className={`p-1.5 rounded-lg ${isCritical ? 'bg-red-100 dark:bg-red-500/20' : 'bg-amber-100 dark:bg-amber-500/20'}`}>
        <Icon size={14} className={isCritical ? 'text-red-500 dark:text-red-400' : 'text-amber-500 dark:text-amber-400'} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-900 dark:text-white truncate">{title}</span>
          <Badge variant={isCritical ? 'critical' : 'warning'}>
            {isCritical ? t('dashboard.alerts.badge.critical') : t('dashboard.alerts.badge.warning')}
          </Badge>
        </div>
        <div className="text-xs text-gray-500 dark:text-slate-400 truncate">{subtitle}</div>
      </div>
      <button className="px-2 py-1 text-xs text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-500/10 rounded-lg transition-colors flex items-center gap-1">
        {action}
        <ChevronRight size={12} className="opacity-0 group-hover:opacity-100 transition-opacity" />
      </button>
    </div>
  );
}

export function AlertsWidget({ alerts = [], onNavigate }) {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(true);

  const criticalCount = alerts.filter(a => a.type === 'critical').length;
  const hasCritical = criticalCount > 0;

  return (
    <Card padding="p-0" className="overflow-hidden">
      {/* Header */}
      <div
        className="flex items-center justify-between p-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-slate-700/30 transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-2">
          <div className={`p-1.5 rounded-lg ${hasCritical ? 'bg-red-100 dark:bg-red-500/20' : 'bg-emerald-100 dark:bg-emerald-500/20'}`}>
            <Bell size={16} className={hasCritical ? 'text-red-500 dark:text-red-400' : 'text-emerald-500 dark:text-emerald-400'} />
          </div>
          <div>
            <div className="font-semibold text-gray-900 dark:text-white text-sm">{t('dashboard.alerts.title')}</div>
            <div className="text-xs text-gray-500 dark:text-slate-400">
              {t('dashboard.alerts.summary', {
                urgent: t('dashboard.alerts.urgent', { count: criticalCount }),
                toHandle: t('dashboard.alerts.toHandle', { count: alerts.length - criticalCount })
              })}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {hasCritical && (
            <Badge variant="critical">{criticalCount}</Badge>
          )}
          {isExpanded ? (
            <ChevronDown size={16} className="text-gray-400 dark:text-slate-400" />
          ) : (
            <ChevronRight size={16} className="text-gray-400 dark:text-slate-400" />
          )}
        </div>
      </div>

      {/* Alert list */}
      {isExpanded && alerts.length > 0 && (
        <div className="border-t border-gray-100 dark:border-slate-700/50">
          {alerts.map((alert, index) => (
            <AlertItem
              key={alert.id}
              alert={alert}
              isLast={index === alerts.length - 1}
              onNavigate={onNavigate}
            />
          ))}
        </div>
      )}

      {/* Empty state */}
      {isExpanded && alerts.length === 0 && (
        <div className="border-t border-gray-100 dark:border-slate-700/50 p-4 text-center">
          <div className="text-emerald-600 dark:text-emerald-400 text-sm">{t('dashboard.alerts.empty')}</div>
          <div className="text-xs text-gray-400 dark:text-slate-500">{t('dashboard.alerts.emptyHint')}</div>
        </div>
      )}
    </Card>
  );
}

export default AlertsWidget;
